import { useState } from 'react'
import { Link as RouterLink } from 'react-router'

import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import Stack from '@mui/material/Stack'
import DialogTitle from '@components/_mui/DialogTitle'

import useDialog from '@shared/hooks/src/useDialog'
import { handleError } from '@shared/utils'

import Confirmation from '@components/Dialog/Confirmation'

import { useTasksFilters } from '../../Tasks.hooks'
import { useMarkCompleted, useMarkNotApplicable } from './TaskInfo.hooks'
import { isNotDone, Patient } from './TaskInfo.utils'

export default function TestOfCureInfo({ data, patient }) {
  const [, updateFilters] = useTasksFilters()
  const [declineOpen, setDeclineOpen] = useState(false)

  const markCompleted = useMarkCompleted(data.id)

  const confirmComplete = useDialog({
    component: Confirmation,
    props: ({ close }) => ({
      title: 'Complete Task?',
      description: 'Please ensure you have ordered labs before completing this task. Labs are required to properly complete this workflow.',
      rejectLabel: 'Cancel',
      confirmLabel: 'Complete',
      onReject: () => close(),
      onConfirm: () => {
        return markCompleted
          .mutateAsync()
          .then(() => close())
          .then(() => updateFilters({ task: undefined }))
          .catch(handleError)
      },
    }),
  })

  if (isNotDone(data)) {
    return (
      <Stack direction="row" spacing={1} sx={{ justifyContent: 'space-between' }}>
        <Button variant="contained" color="error" onClick={() => setDeclineOpen(true)}>
          Not Applicable
        </Button>
        <Stack direction="row" spacing={1}>
          <Button variant="contained" component={RouterLink} to={`/app/patients/${patient.id}/encounters/${data.relatedModelId}`}>
            Order Labs
          </Button>
          <Button variant="contained" onClick={confirmComplete}>
            Complete
          </Button>
        </Stack>
        <Dialog open={declineOpen} onClose={() => setDeclineOpen(false)} fullWidth maxWidth="xs">
          <DeclineContent task={data} patient={patient} onClose={() => setDeclineOpen(false)} />
        </Dialog>
      </Stack>
    )
  }
}

function DeclineContent({ task, patient, onClose }) {
  const [, updateFilters] = useTasksFilters()
  const markNotApplicable = useMarkNotApplicable(task.id)

  const handleDecline = () => {
    return markNotApplicable
      .mutateAsync('Labs not needed')
      .then(() => updateFilters({ task: undefined }))
      .catch(handleError)
  }

  return (
    <>
      <DialogTitle>Not Applicable</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={1}>
          <Patient user={patient} />
          <Alert severity="warning" variant="border">
            Choosing "Not Applicable" will remove this task and not order the patient follow up labs.
          </Alert>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="error">
          Cancel
        </Button>
        <Button loading={markNotApplicable.isPending} onClick={handleDecline} color="error" variant="contained">
          Confirm
        </Button>
      </DialogActions>
    </>
  )
}
